import { ButtonBase, Divider, ListItemIcon, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useActiveModule } from './useActiveModule';
import { startEdge } from './menuOrigin';
import { MODULE_REGISTRY, openableModules } from '@/modules/registry';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { radii } from '@/theme/tokens';

/**
 * The one way between modules: a plain MUI `Menu` on a top-bar button — no
 * hand-rolled popper, and nothing moves.
 *
 * The button states which module you are in, with the module's own icon,
 * because the sidebar alone does not say it at a glance once the rail is
 * collapsed. "All apps" is always offered here, even though `AllAppsButton`
 * sits immediately before this button and goes to the same place: this entry
 * is the one a viewer with a single module has (the button is not drawn for
 * them), and it is the exit for someone who opened the menu to browse and
 * found no module they wanted. The grid stays a real page for everyone, so the
 * switcher never bounces.
 */
export function ModuleSwitcher() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { permissions, isPlatform } = usePermissions();
  const { module } = useActiveModule();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const modules = openableModules(MODULE_REGISTRY, { isPlatform, permissions });
  const CurrentIcon = module ? module.icon : GridViewRoundedIcon;

  const go = (to: string) => {
    setAnchor(null);
    navigate(to);
  };

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={t('modules.switch')}
        sx={{
          height: 38,
          px: 1.5,
          gap: 1,
          minWidth: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: `${radii.sm}px`,
          bgcolor: anchor ? 'brand.selected' : 'background.paper',
          color: 'text.primary',
          fontFamily: 'inherit',
          '&:hover': { borderColor: 'brand.borderStrong' },
          '&.Mui-focusVisible': { boxShadow: (th) => `0 0 0 3px ${th.palette.brand.focusRing}` },
          '& > svg': { flexShrink: 0 },
        }}
      >
        <CurrentIcon sx={{ fontSize: 18, color: module ? 'text.primary' : 'text.secondary' }} />
        {/* Arabic module names are longer than the English: capped and never wrapped. */}
        <Typography
          noWrap
          component="span"
          sx={{ fontSize: '0.875rem', fontWeight: module ? 600 : 500, maxWidth: { xs: 90, sm: 200 } }}
        >
          {t(module ? module.titleKey : 'modules.allApps')}
        </Typography>
        <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: startEdge(theme.direction) }}
        transformOrigin={{ vertical: 'top', horizontal: startEdge(theme.direction) }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        {modules.map((entry) => {
          const Icon = entry.icon;
          const current = entry.key === module?.key;
          return (
            <MenuItem
              key={entry.key}
              selected={current}
              aria-current={current ? 'true' : undefined}
              onClick={() => go(entry.to as string)}
            >
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <Typography variant="inherit" noWrap>
                {t(entry.titleKey)}
              </Typography>
            </MenuItem>
          );
        })}
        <Divider sx={{ mx: 0.75, my: '4px !important' }} />
        <MenuItem onClick={() => go(paths.dashboard)}>
          <ListItemIcon>
            <SpaceDashboardRoundedIcon />
          </ListItemIcon>
          {t('nav.dashboard')}
        </MenuItem>
        <MenuItem
          onClick={() => go(paths.modules)}
          sx={{ color: 'brand.primaryInk', fontWeight: 600, '& .MuiListItemIcon-root': { color: 'brand.primaryInk' } }}
        >
          <ListItemIcon>
            <GridViewRoundedIcon />
          </ListItemIcon>
          {t('modules.allApps')}
        </MenuItem>
      </Menu>
    </>
  );
}
